<template>
  <div class="twilio-status-badge">
    <!-- Trigger button in navbar -->
    <b-nav-item
      id="twilio-status-btn"
      class="twilio-status-nav-item"
      href="https://status.twilio.com"
      target="_blank"
      rel="noopener noreferrer"
      @click.prevent
    >
      <div
        class="d-flex align-items-center twilio-trigger"
        :style="{
          '--twilio-bg-color': overallColor + '1a',
          '--twilio-hover-bg-color': overallColor + '2d',
          borderRadius: '2rem',
          paddingInline: '1rem',
          paddingTop: '0.3rem',
          paddingBottom: '0.3rem',
          cursor: 'pointer'
        }"
      >
        <!-- Pulse dot -->
        <span
          class="twilio-dot"
          :class="{ 'twilio-dot--pulse': overallStatus !== 'operational' && overallStatus !== 'loading' && overallStatus !== 'error' }"
          :style="{ backgroundColor: overallColor }"
        />
        <span class="twilio-label d-none d-md-inline ml-50">Twilio</span>
        <!-- Spinner while loading -->
        <b-spinner
          v-if="overallStatus === 'loading'"
          small
          class="ml-50"
          style="width: 10px; height: 10px;"
        />
      </div>
    </b-nav-item>

    <!-- Popover detail panel -->
    <twilio-status-popover
      :overall-status="overallStatus"
      :services="services"
      :active-incidents="activeIncidents"
      :last-updated-label="lastUpdatedLabel"
    />
  </div>
</template>

<script>
import { BNavItem, BSpinner } from 'bootstrap-vue'
import {
  TWILIO_STATUS_URL,
  WATCHED_COMPONENT_IDS,
  STATUS_CONFIG,
  STATUS_PRIORITY,
  POLL_INTERVAL_MS,
} from './twilioStatusConfig'
import TwilioStatusPopover from './TwilioStatusPopover.vue'

export default {
  name: 'TwilioStatusBadge',

  components: {
    BNavItem,
    BSpinner,
    TwilioStatusPopover,
  },

  data() {
    return {
      overallStatus: 'loading',
      services: WATCHED_COMPONENT_IDS.map(c => ({ ...c, status: 'loading' })),
      activeIncidents: [],
      lastUpdated: null,
      pollTimer: null,
    }
  },

  computed: {
    overallColor() {
      if (this.overallStatus === 'loading' || this.overallStatus === 'error') {
        return '#b9b9c3'
      }
      return STATUS_CONFIG[this.overallStatus]?.color ?? '#b9b9c3'
    },
    isAnomalous() {
      return !['operational', 'loading', 'error'].includes(this.overallStatus)
    },
    lastUpdatedLabel() {
      if (!this.lastUpdated) { return 'Jamais mis à jour' }
      const diff = Math.round((Date.now() - this.lastUpdated) / 1000)
      return diff < 60 ? `il y a ${diff}s` : `il y a ${Math.round(diff / 60)}min`
    },
  },

  mounted() {
    this.fetchStatus()
    this.pollTimer = setInterval(this.fetchStatus, POLL_INTERVAL_MS)
  },

  beforeDestroy() {
    clearInterval(this.pollTimer)
  },

  methods: {
    async fetchStatus() {
      try {
        const response = await fetch(TWILIO_STATUS_URL, {
          method: 'GET',
          headers: { Accept: 'application/json' },
        })
        if (!response.ok) { throw new Error(`HTTP ${response.status}`) }
        this.processData(await response.json())
      } catch {
        this.overallStatus = 'error'
      }
    },

    processData(data) {
      const componentMap = Object.fromEntries(
        (data.components ?? []).map(c => [c.id, c.status]),
      )

      this.services = WATCHED_COMPONENT_IDS.map(watched => ({
        ...watched,
        status: componentMap[watched.id] ?? 'unknown',
      }))

      this.overallStatus = STATUS_PRIORITY.find(
        p => this.services.some(s => s.status === p),
      ) ?? 'operational'

      const watchedIds = new Set(WATCHED_COMPONENT_IDS.map(c => c.id))
      const activeStatuses = new Set(['investigating', 'identified', 'monitoring'])
      this.activeIncidents = (data.incidents ?? [])
        .filter(i => activeStatuses.has(i.status) && i.components?.some(c => watchedIds.has(c.id)))
        .slice(0, 3)

      this.lastUpdated = Date.now()
    },
  },
}
</script>

<style scoped>
.twilio-status-nav-item :deep(.nav-link),
.twilio-status-nav-item :deep(a.nav-link) {
  border-top: none !important;
  border-bottom: none !important;
  box-shadow: none !important;
  outline: none !important;
}

.twilio-trigger {
  background: var(--twilio-bg-color);
  transition: background 0.2s ease;
}

.twilio-trigger:hover {
  background: var(--twilio-hover-bg-color) !important;
}

.twilio-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  transition: background-color 0.4s ease;
}

.twilio-dot--pulse {
  animation: twilio-pulse 2s infinite;
}

@keyframes twilio-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(40, 199, 111, 0.4); }
  70%  { box-shadow: 0 0 0 6px rgba(40, 199, 111, 0); }
  100% { box-shadow: 0 0 0 0 rgba(40, 199, 111, 0); }
}

.twilio-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: inherit;
  letter-spacing: -0.01em;
}
</style>
